import { err, ok, Result } from '@easeltv/fail';
import { LoggerInstance } from '@easeltv/logger';
import {
    chunkBulkRequest,
    NoContentResponse,
    RequestResult,
    ResponseSchemaInvalidFail,
} from '@easeltv/remote';
import { dates, is } from '@easeltv/utilities';

import { CachedSDK } from './caching';
import { mapOptionsToStrings } from './helpers';
import { ISO6391 } from './ISO-639-1';
import {
    AgeRating,
    AllAgeRatings,
    AllBanners,
    AllBannersResponse,
    AllCategories,
    AllCategoryTypes,
    AllCollectionsResponse,
    AllLabels,
    AllPersons,
    AllPersonTypes,
    AllProductsResponse,
    Banner,
    BannerResponse,
    BaseDictionary,
    Category,
    CategoryType,
    ChangePasswordRequestBody,
    Classification,
    Collection,
    CompleteRegistration,
    Config,
    Dictionary,
    Footer,
    ForgotPasswordRequestBody,
    Label,
    LoginRequestBody,
    LoginResponse,
    Menu,
    Page,
    PairingResponse,
    Person,
    PersonProducts,
    Product,
    ProductMediaFeature,
    ProductMediaTrailer,
    PurchaseEventsResponse,
    PurchaseIntent,
    RegistrationCreateRequestBody,
    RegistrationRequestResponse,
    RegistrationUpdateRequestBody,
    RenewalPeriod,
    RequiresActionIntent,
    ResetPasswordBody,
    ResetPasswordResponse,
    SearchResponse,
    Section,
    SectionChildren,
    SectionCollection,
    SectionProduct,
    SubscriptionIntent,
    SubscriptionPaymentMethod,
    SubscriptionResponse,
    Theme,
    UpdateCardIntent,
    User,
    UserPaymentsResponse,
    VerifiedPurchaseCode,
    VerifyEmailResponse,
} from './models';
import { PlayerEventBody } from './models/player';
import {
    BulkRequestOptions,
    ExtendRequestOptions,
    PageRequestOptions,
} from './types';

/* -------------------------------- Constants ------------------------------- */

/**
 * DEFAULT_CACHE_DURATION
 *
 * In leu of a duration from the config, this should be used instead
 */
const DEFAULT_CACHE_DURATION = 'P0DT0H0M0.000S';

/**
 * Endpoint URLs
 *
 * Object containing the endpoints for specific model requests - used in the ClientSDK class to build requests
 */
const urls = {
    ageRating: {
        get: '/v1/age-rating/:id',
        all: '/v1/age-rating',
    },
    category: {
        all: '/v1/category',
        get: '/v1/category/:id',
    },
    categoryType: {
        all: '/v1/category-type',
        get: '/v1/category-type/:id',
    },
    collection: {
        all: '/v1/collection',
        get: '/v1/collection/:id',
        getByReference: '/v1/collection/by-reference/:reference',
    },
    config: '/v1/config',
    dictionary: '/v1/dictionary',
    favourite: '/v1/favourite/:id',
    label: {
        all: '/v1/label',
        get: '/v1/label/:id',
    },
    menu: '/v2/menu',
    page: {
        all: '/v1/page',
        get: '/v1/page/:id',
        getByReference: '/v1/page/by-reference/:reference',
        getByReferenceAuto: '/v1/page/by-reference',
    },
    password: {
        forgot: '/v1/user/initiate-password-reset',
        reset: '/v1/user/complete-password-reset',
        change: '/v1/user/change-password',
    },
    person: {
        all: '/v1/person',
        get: '/v1/person/:id',
        getByReference: '/v1/person/by-reference/:reference',
        products: '/v1/person/:id/product',
    },
    personType: {
        all: '/v1/person-type',
    },
    playerEvent: '/v1/player-event',
    product: {
        all: '/v1/product',
        get: '/v1/product/:id',
        getByReference: '/v1/product/by-reference/:reference',
        trailer: '/v1/product/:id/media/trailer',
        feature: '/v1/product/:id/media/feature',
        people: '/v1/product/:id/person',
    },
    svod: {
        stripe: '/v1/subscription/subscribe/stripe',
        getPaymentMethod: '/v1/subscription/payment-method',
        updatePaymentMethodIntent: '/v1/subscription/payment-method',
        requiresActionIntent: '/v1/subscription/:id/requires-action',
        cancel: '/v1/subscription/:id/unsubscribe',
    },
    purchase: {
        purchase: '/v1/purchase',
        event: '/v1/purchase/:id/event',
        verify: '/v1/purchase/code/:code',
    },
    registration: {
        start: '/v1/registration',
        update: '/v1/registration/:id',
        complete: '/v1/registration/:id/complete',
        verify: '/v1/registration/verify-email/:key',
    },
    search: {
        get: '/v2/search/:query',
    },
    session: {
        create: '/v1/session',
        login: '/v1/session/login',
        logout: '/v1/session/logout',
        pair: '/v1/session/pair/:code',
    },
    subscription: '/v1/subscription-page',
    pairing: {
        validate: '/v1/pairing/:code',
    },
    banner: {
        get: '/v1/banner/:id',
        all: '/v1/banner',
        getByCategory: '/v1/banner/by-category',
        getByCategoryBulk: '/v1/banner/by-category-bulk',
    },
    footer: '/v1/footer/:id',
    theme: '/v1/theme',
    user: {
        get: '/v1/user',
        update: '/v1/user',
        payments: '/v1/user/payment',
    },
};

/* ---------------------------------- Types --------------------------------- */

/**
 * ClientSdkI
 */
interface ClientSdkI {
    /** Needed to make the initial session and config requests */
    apiKey?: string;

    /** Flag to disable all fetching from data cache */
    disableCaching?: boolean;

    /** Environment determines the structure of the base URL */
    env?: string;

    /** Copy of the cache to restore from at session start */
    restoredCache: string | undefined;

    /** Tenant injected into the tenant header */
    tenant?: string;

    logger?: LoggerInstance;

    refreshTokenStorage: {
        get: () => string | undefined;
        set: (token: string) => void;
    };
}

/* -------------------------------- ClientSDK ------------------------------- */

/**
 * ClientSDK
 *
 * The main SDK object to be consumed
 */
export class ClientSDK extends CachedSDK {
    /** Environment determines the structure of the base URL */
    env: string;

    constructor({
        tenant = 'etv',
        env = 'dev',
        apiKey = 'zq5pyPd0RTbNg3Fyj52PrkKL9c2Af38HHh4itgZTKDaCzjAyhd',
        disableCaching = false,
        restoredCache,
        logger,
        refreshTokenStorage,
    }: ClientSdkI) {
        super(
            `https://${env}.suggestedtv.com/api/client`,
            tenant,
            apiKey,
            refreshTokenStorage,
            restoredCache,
            logger,
        );

        this.disableCaching = disableCaching;
        this.tenant = tenant;
        this.env = env;
    }

    /* -------------------------------------------------------------------------- */
    /* -------------------------------- Functions ------------------------------- */
    /* -------------------------------------------------------------------------- */

    /* ------------------------------- Age Rating ------------------------------- */

    /**
     * getAgeRating
     *
     * Get a Specific Age Rating by ID
     *
     * @param id - ID of the Age Rating
     * @param { bust } - Override to force the cache not to be used
     * @returns
     */
    getAgeRating = async (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.ageRating.get, {
                    validator: AgeRating,
                })
                    .withParams({ id: identifier })
                    .send(),
            {
                cache: { key: 'ageRating', prop: 'id', value: id },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.ageRating.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * allAgeRatings
     *
     * @param queries - A list of ageRating ids
     * @returns A list of AgeRating objects
     */
    getAllAgeRatings = (
        queries: BulkRequestOptions & PageRequestOptions,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.ageRating.all, { validator: AllAgeRatings })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'ageRating',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.ageRating.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------ Authorization ----------------------------- */

    /**
     * login
     *
     * Logs user in so session is linked to user account
     *
     * @param body - The email and password required to login
     * @returns A RefreshToken
     */
    login = async ({ email, password }: LoginRequestBody) =>
        this.post(urls.session.login, {
            validator: LoginResponse,
        })
            .withBody({ email, password })
            .send();

    /**
     * logout
     *
     * Logs user out unlinking session token from account
     *
     * @returns No Content
     */
    logout = async () =>
        this.post(urls.session.logout, {
            validator: NoContentResponse,
        }).send();

    /* --------------------------------- Banners -------------------------------- */

    /**
     * getBanner
     *
     * Get a single Banner
     *
     * @param id - The Banner ID
     * @param { bust } - Override to force the cache not to be used
     * @returns A Banner object
     */
    getBanner = async (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (bannerId) =>
                this.get(urls.banner.get, {
                    validator: Banner,
                })
                    .withParams({ id: bannerId })
                    .send(),
            {
                cache: {
                    key: 'banner',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.banner.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getFooter
     *
     * Get a single Footer
     *
     * @param id - The Footer id
     * @param { bust } - Override to force the cache not to be used
     * @returns A Footer object
     */
    getFooter = async (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (footerId) =>
                this.get(urls.footer, {
                    validator: Footer,
                })
                    .withParams({ id: footerId })
                    .send(),
            {
                cache: {
                    key: 'footer',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.footer.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getBannerByCategory
     *
     * Gets the id for a Banner for the provided list of category ids
     * Not cached as we want up to date lists
     *
     * @param ids - A list of category ids
     * @param { bust } - Override to force the cache not to be used
     * @returns A Banner object
     */
    getBannerByCategory = async (ids: string[]) => {
        const result = await this.post(urls.banner.getByCategory, {
            validator: BannerResponse,
        })
            .withBody({ categories: ids })
            .send();

        return result;
    };

    /**
     * getBanners
     *
     * Gets a collection of Banners
     *
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of Banner objects
     */
    getBanners = async (
        queries: BulkRequestOptions & PageRequestOptions,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.banner.all, { validator: AllBanners })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'banner',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.banner.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getBannersByCategory
     *
     * Gets a list of Banner ids for the provided list of category ids
     * Not cached as we want up to date lists
     *
     * @param ids - A list of category ids
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of Banner IDs
     */
    getBannersByCategory = async (ids: string[]) => {
        const result = await this.post(urls.banner.getByCategory, {
            validator: AllBannersResponse,
        })
            .withBody({ categories: ids })
            .send();

        return result;
    };

    /* -------------------------------- Category -------------------------------- */

    /**
     * allCategories
     *
     * Get all Categories or a specific batch of Categories by ID
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of Categories
     */
    allCategories = (
        queries: BulkRequestOptions & PageRequestOptions,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.category.all, { validator: AllCategories })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'category',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.category.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getCategory
     *
     * Get a Specific Category by ID
     *
     * @param id - The ID of the Category
     * @param { bust } - Override to force the cache not to be used
     * @returns A Category object
     */
    getCategory = (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.category.get, { validator: Category })
                    .withParams({ id: identifier })
                    .send(),
            {
                cache: {
                    key: 'category',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.category.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------ Category Type ----------------------------- */

    /**
     * allCategoryTypes
     *
     * Get all CategoryTypes or a specific batch of CategoryTypes by ID.
     *
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of CategoryTypes
     */
    allCategoryTypes = (
        queries: BulkRequestOptions & PageRequestOptions,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.categoryType.all, { validator: AllCategoryTypes })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'categoryType',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.categoryType.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getCategoryType
     *
     * Get a Specific CategoryType by ID
     *
     * @param id - The ID of the CategoryType
     * @param { bust } - Override to force the cache not to be used
     * @returns A CategoryType object
     */
    getCategoryType = (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.categoryType.get, { validator: CategoryType })
                    .withParams({ id: identifier })
                    .send(),
            {
                cache: {
                    key: 'categoryType',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.categoryType.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------- Collections ------------------------------ */

    /**
     * allCollections
     *
     * Get all Collections or a specific batch of Collections by ID
     *
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of Collections
     */
    allCollections = (
        queries: BulkRequestOptions &
            PageRequestOptions &
            ExtendRequestOptions<Array<'label'>>,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.collection.all, {
                    validator: AllCollectionsResponse,
                })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'collection',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.collection.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getCollection
     *
     * Get a Specific Collection by ID
     *
     * @param id - The ID of the Collection
     * @param { bust } - Override to force the cache not to be used
     * @returns A Collection object
     */
    getCollection = (
        id: string,
        queries?: ExtendRequestOptions<Array<'label'>>,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.collection.get, { validator: Collection })
                    .withParams({ id: identifier })
                    .withQueries(mapOptionsToStrings(queries))
                    .send(),
            {
                cache: {
                    key: 'collection',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.collection.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getCollectionByRef
     *
     * Get a specific Collection by the unique reference string
     *
     * @param reference - The reference of the Collection
     * @param { bust } - Override to force the cache not to be used
     * @returns A Collection object
     */
    getCollectionByRef = (
        reference: string,
        options?: ExtendRequestOptions<Array<'label'>>,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.collection.getByReference, {
                    validator: Collection,
                })
                    .withParams({ reference: identifier })
                    .withQueries(mapOptionsToStrings(options))
                    .send(),
            {
                cache: {
                    key: 'collection',
                    prop: 'reference',
                    value: reference,
                },
                bust: bust ?? Boolean(options?.extend),
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.collection.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* --------------------------------- Config --------------------------------- */

    /**
     * getConfig
     *
     * Get the Config object
     *
     * @param { bust } - Override to force the cache not to be used
     * @returns A Config object
     */
    getConfig = ({ bust }: { bust?: boolean } = {}) =>
        this.getFirstCachedResource(
            () =>
                this.get(urls.config, { validator: Config })
                    .withHeaders({
                        'api-key': this.apiKey,
                        'Content-Type': 'application/json',
                    })
                    .send(),
            {
                cache: {
                    key: 'config',
                },
                bust,
                mode: (config) => config.cache.mode,
                expiry: (config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.config.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------- Dictionary ------------------------------- */

    /**
     * getDictionary
     *
     * Get the Dictionary object
     *
     * @param lang ISO-639-1 language codes eg. en, fr, de
     * @param { bust } - Override to force the cache not to be used
     * @returns A Dictionary object
     */
    getDictionary = (
        lang: `${ISO6391}`,
        { bust }: { bust?: boolean } = {},
    ): Promise<RequestResult<BaseDictionary>> =>
        this.getCachedResource(
            (language) =>
                this.get(urls.dictionary, { validator: Dictionary })
                    .withHeaders({
                        session: this.session || '',
                        'Accept-Language': language,
                    })
                    .send(),
            {
                cache: {
                    key: 'dictionary',
                    prop: 'language',
                    value: lang,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.dictionary.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------- Favourite ------------------------------- */

    /**
     * favourite
     *
     * Favourites an item
     *
     * @param id - The item ID of the item to favourite
     * @returns No Content
     */
    favourite = (id: string) =>
        this.post(urls.favourite, {
            validator: NoContentResponse,
        })
        .withParams({ id })
        .send();

    /**
     * unfavourite
     *
     * Unfavourites an item
     *
     * @param id - The item ID of the item to unfavourite
     * @returns No Content
     */
    unfavourite = (id: string) =>
        this.del(urls.favourite, {
            validator: NoContentResponse,
        })
        .withParams({ id })
        .send();

    /* ---------------------------------- Label --------------------------------- */

    /**
     * allLabels()
     *
     * Get all labels or a specific batch of labels by ID
     *
     * @param queries Options to be sent with the bulk request - includes paging and sorting options
     * @returns
     */
    allLabels = (
        queries: BulkRequestOptions & PageRequestOptions,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.label.all, { validator: AllLabels })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'label',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.label.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * preloadLabels()
     *
     * Get all labels or a specific batch of labels by ID
     *
     * @param queries Options to be sent with the bulk request - includes paging and sorting options
     * @returns
     */
    preloadLabels = () =>
        this.preloadCacheableResources(
            () =>
                this.get(urls.label.all, { validator: AllLabels })
                    .withQueries({ limit: 100 })
                    .send(),
            {
                cache: {
                    key: 'label',
                },
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.label.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getLabel()
     *
     * Get a Specific label by ID
     *
     * @param id ID of the label
     * @returns
     */
    getLabel = (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (labelId) =>
                this.get(urls.label.get, {
                    validator: Label,
                })
                    .withParams({ id: labelId })
                    .send(),
            {
                cache: {
                    key: 'label',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.label.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ---------------------------------- Menu ---------------------------------- */

    /**
     * getMenu
     *
     * Get the Menu elements array
     *
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of MenuElements
     */
    getMenu = (
        options: ExtendRequestOptions<Array<'pages'>>,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getFirstCachedResource(
            () =>
                this.get(urls.menu, { validator: Menu })
                    .withQueries(mapOptionsToStrings(options))
                    .send(),
            {
                cache: {
                    key: 'menu',
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.menu.expire || DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ---------------------------------- Page ---------------------------------- */

    /**
     * getPage
     *
     * Get a specific Page by ID
     *
     * @param id - The ID of the Page
     * @param { bust } - Override to force the cache not to be used
     * @returns A Page object
     */
    getPage = (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.page.get, { validator: Page })
                    .withParams({ id: identifier })
                    .send(),
            {
                cache: {
                    key: 'page',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.page.expire || DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getPageByRef
     *
     * Get a specific Page by the unique reference string
     *
     * @param reference - The reference of the Page
     * @param { bust } - Override to force the cache not to be used
     * @returns A Page object
     */
    getPageByRef = (reference?: string, { bust }: { bust?: boolean } = {}) => {
        // @TODO: Cache the homepage without ref
        if (!reference)
            return this.get(urls.page.getByReferenceAuto, {
                validator: Page,
            }).send();

        return this.getCachedResource(
            (ref) =>
                this.get(urls.page.getByReference, { validator: Page })
                    .withParams({ reference: ref })
                    .send(),
            {
                cache: {
                    key: 'page',
                    prop: 'reference',
                    value: reference,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.page.expire || DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );
    };

    /* --------------------------------- Pairing -------------------------------- */

    /**
     * validatePairingCode
     *
     * Validates if a pairing code is valid and can be used to pair with
     *
     * @param code - The pairing code to be validated
     * @returns No Content
     */
    validatePairingCode = (code: string) =>
        this.get(urls.pairing.validate, {
            validator: NoContentResponse,
        })
            .withParams({ code })
            .send();

    /**
     * pairDevice
     *
     * Pairs a device with a user account
     *
     * @param pairingCode - The pairing code from the device
     * @returns A PairingResponse object
     */
    pairDevice = (pairingCode: string) =>
        this.post(urls.session.pair, { validator: PairingResponse })
            .withParams({
                code: pairingCode,
            })
            .send();

    /* -------------------------------- Password -------------------------------- */

    /**
     * changePassword
     *
     * Update a the current users Password with a new Password
     *
     * @param body - The information required to update a Password
     * @returns No Content
     */
    changePassword = (body: ChangePasswordRequestBody) =>
        this.post(urls.password.change, { validator: NoContentResponse })
            .withBody({
                existingPassword: body.existingPassword,
                newPassword: body.newPassword,
            })
            .send();

    /**
     * forgotPassword
     *
     * Requests a Password Reset email, if it belongs to a registered user
     *
     * @param { email } - The email for the account that wants to reset its Password
     * @returns No Content
     */
    forgotPassword = ({ email }: ForgotPasswordRequestBody) =>
        this.post(urls.password.forgot, { validator: NoContentResponse })
            .withBody({ email })
            .send();

    /**
     * resetPassword
     *
     * Updates the password of the account linked to the unique key generated by the forgotPassword method
     *
     * @param { key, password } - The key that authorized the Password change and the new Password
     * @returns An object containing a new RefreshToken
     */
    resetPassword = async ({ key, password }: ResetPasswordBody) =>
        this.post(urls.password.reset, {
            validator: ResetPasswordResponse,
        })
            .withBody({ key, password })
            .send();

    /* --------------------------------- Person --------------------------------- */

    /**
     * getPerson
     *
     * Get a Specific Person by ID
     *
     * @param id - The ID of the Person
     * @param { bust } - Override to force the cache not to be used
     * @returns A Person object
     */
    getPerson = (id: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.person.get, { validator: Person })
                    .withParams({ id: identifier })
                    .send(),
            {
                cache: {
                    key: 'person',
                    prop: 'id',
                    value: id,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.person.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getPersonByRef
     *
     * Get a Specific Person by reference
     *
     * @param id - The ID of the Person
     * @param { bust } - Override to force the cache not to be used
     * @returns A Person object
     */
    getPersonByRef = (reference: string, { bust }: { bust?: boolean } = {}) =>
        this.getCachedResource(
            (identifier) =>
                this.get(urls.person.getByReference, { validator: Person })
                    .withParams({ reference: identifier })
                    .send(),
            {
                cache: {
                    key: 'person',
                    prop: 'reference',
                    value: reference,
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.person.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ----------------------------- Person Products ---------------------------- */

    /**
     * getProductTrailer
     *
     * Fetches the Trailer stream information for a given Product
     *
     * @param id - The ID of the Product
     * @returns A ProductTrailer object
     */
    getPersonProducts = (id: string) =>
        this.get(urls.person.products, {
            validator: PersonProducts,
        })
            .withParams({ id })
            .send();

    /* --------------------------------- Persons -------------------------------- */

    /**
     * getPersons
     *
     * Get all Persons or a specific batch of Persons by ID
     *
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of Persons
     */
    getPersons = (
        queries: BulkRequestOptions,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.person.all, { validator: AllPersons })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'person',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.person.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------- Person Type ------------------------------ */

    /**
     * getPersonTypes
     *
     * Get all PersonTypes or a specific batch of PersonTypes by ID
     *
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * @param { bust } - Override to force the cache not to be used
     * @returns An array of PersonTypes
     */

    getPersonTypes = (
        queries: BulkRequestOptions,

        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.personType.all, { validator: AllPersonTypes })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'personType',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.personType.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ------------------------------ Player Event ------------------------------ */

    /**
     * playerEvent
     *
     * Sends player events to be tracked
     *
     * @param body - Information about the PlayerEvent
     * @returns No Content
     */
    playerEvent = (body: PlayerEventBody) =>
        this.post(urls.playerEvent, { validator: NoContentResponse })
            .withBody(body)
            .send();

    /* --------------------------------- Product -------------------------------- */

    /**
     * allProducts
     *
     * Get all Products or a specific batch of Products by ID
     *
     * @param queries - Options to be sent with the bulk request - includes paging and sorting options
     * * @param { bust } - Override to force the cache not to be used
     * @returns An array of Products
     */
    allProducts = (
        queries: BulkRequestOptions &
            PageRequestOptions &
            ExtendRequestOptions<
                Array<
                    | 'availability'
                    | 'external-links'
                    | 'feature'
                    | 'label'
                    | 'progress'
                    | 'seo'
                    | 'trailer'
                >
            >,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResources(
            (ids) =>
                this.get(urls.product.all, { validator: AllProductsResponse })
                    .withQueries(mapOptionsToStrings({ ...queries, ids }))
                    .send(),
            {
                cache: {
                    key: 'product',
                    prop: 'id',
                    value: queries?.ids || [],
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.product.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getProduct
     *
     * Get a specific Product by ID
     *
     * @param id - The ID of the Product
     * @param options - Options to extend the Product to get extra information
     * @param { bust } - Override to force the cache not to be used
     * @returns A Product object
     */
    getProduct = async (
        id: string,
        options?: ExtendRequestOptions<
            Array<
                | 'availability'
                | 'external-links'
                | 'favourite'
                | 'feature'
                | 'label'
                | 'progress'
                | 'seo'
                | 'trailer'
            >
        >,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResource(
            (productId) =>
                this.get(urls.product.get, {
                    validator: Product,
                })
                    .withParams({ id: productId })
                    .withQueries(mapOptionsToStrings(options))
                    .send(),
            {
                cache: {
                    key: 'product',
                    prop: 'id',
                    value: id,
                },
                bust: bust ?? Boolean(options?.extend),
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.product.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getProduct
     *
     * Get a specific Product by reference
     *
     * @param reference - The reference of the Product
     * @param options - Options to extend the Product to get extra information
     * @param { bust } - Override to force the cache not to be used
     * @returns A Product object
     */
    getProductByReference = async (
        reference: string,
        options?: ExtendRequestOptions<
            Array<
                | 'availability'
                | 'external-links'
                | 'favourite'
                | 'feature'
                | 'label'
                | 'progress'
                | 'seo'
                | 'trailer'
            >
        >,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getCachedResource(
            (ref) =>
                this.get(urls.product.getByReference, {
                    validator: Product,
                })
                    .withParams({ reference: ref })
                    .withQueries(mapOptionsToStrings(options))
                    .send(),
            {
                cache: {
                    key: 'product',
                    prop: 'reference',
                    value: reference,
                },
                bust: bust ?? Boolean(options?.extend),
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.product.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /**
     * getProductFeature
     *
     * Fetches the Feature stream information for a given Product
     *
     * @param id - The ID of the Product
     * @returns A ProductFeature object
     */
    getProductFeature = (id: string) =>
        this.get(urls.product.feature, {
            validator: ProductMediaFeature,
        })
            .withParams({ id })
            .send();

    /**
     * getProductTrailer
     *
     * Fetches the Trailer stream information for a given Product
     *
     * @param id - The ID of the Product
     * @returns A ProductTrailer object
     */
    getProductTrailer = (id: string) =>
        this.get(urls.product.trailer, {
            validator: ProductMediaTrailer,
        })
            .withParams({ id })
            .send();

    /* -------------------------------- Purchase -------------------------------- */

    /**
     * createPurchaseIntent
     *
     * Creates a purchase intent object on the server and returns the id here
     *
     * @param productId - The ID of the product being purchased
     * @returns Purchase intent object
     */
    createPurchaseIntent = (productId: string) =>
        this.post(urls.purchase.purchase, {
            validator: PurchaseIntent,
        })
            .withBody({ productId })
            .send();

    /**
     * getPurchaseEvents
     *
     * Gets an array of purchase events for the given product to check if purchase has been successful
     *
     * @param id - The ID of the Stripe purchase to check events for
     * @returns An array of purchase events relating to this purchase
     */
    getPurchaseEvents = (id: string) =>
        this.get(urls.purchase.event, { validator: PurchaseEventsResponse })
            .withParams({ id })
            .send();

    /**
     * verifyPurchaseCode
     *
     * Verifies a code and returns the purchase information initiated on a device
     *
     * @param code - The purchase code
     * @returns Object containing product id and payment intent information
     */
    verifyPurchaseCode = (code: string) =>
        this.get(urls.purchase.verify, { validator: VerifiedPurchaseCode })
            .withParams({ code })
            .send();

    /* ------------------------------ Registration ------------------------------ */

    /**
     * completeRegistration
     *
     * Confirms and validated the information already given and completes the registration process
     *
     * @param id - The ID of the registration process
     * @returns A RegisteredUser object
     */
    completeRegistration = async (id: string) =>
        this.post(urls.registration.complete, {
            validator: CompleteRegistration,
        })
            .withParams({ id })
            .send();

    /**
     * startRegistration
     *
     * Starts the registration process and saves the initially given information
     *
     * @param registrationBody - Fields required for Registration
     * @returns The ID of the registration process
     */
    startRegistration = (registrationBody: RegistrationCreateRequestBody) =>
        this.post(urls.registration.start, {
            validator: RegistrationRequestResponse,
        })
            .withBody(registrationBody)
            .send();

    /**
     * updateRegistration
     *
     * Updated the Registration process
     *
     * @param id - The ID of the registration process
     * @param registrationBody - Fields required for Registration
     * @returns The ID of the registration process
     */
    updateRegistration = (
        id: string,
        registrationBody: RegistrationUpdateRequestBody,
    ) =>
        this.put(urls.registration.update, {
            validator: RegistrationRequestResponse,
        })
            .withParams({ id })
            .withBody({
                id,
                ...registrationBody,
            })
            .send();

    /**
     * verifyEmail
     *
     * Submits the key provided by email to verify the users email address
     *
     * @param key - The key sent via email to the user
     * @returns The ID of the registration process
     */
    verifyEmail = (key: string, abortController: AbortController) =>
        this.post(urls.registration.verify, {
            validator: VerifyEmailResponse,
            abortController,
        })
            .withParams({ key })
            .send();

    /* --------------------------------- Search --------------------------------- */

    /**
     * searchProducts
     *
     * Fetches a list of products that match the given search query
     *
     * @param query - A search query string
     * @returns An array of Products
     */
    search = async (
        query: string,
        showAgeRatings: boolean,
        abortController?: AbortController,
    ): Promise<RequestResult<{ data: SectionChildren[] }>> => {
        const searchResult = await this.get(urls.search.get, {
            validator: SearchResponse,
            abortController,
        })
            .withParams({ query })
            .send();

        if (!searchResult.ok) {
            return searchResult;
        }

        const itemIds = searchResult.payload.body.data;

        const fetchCollections = async (
            collectionIds: string[],
        ): Promise<SectionCollection[]> => {
            // Page element collection may have no children that are collections.
            // If this is the case we want to avoid making a request.
            if (collectionIds.length < 1) {
                return [];
            }

            const result = await chunkBulkRequest(collectionIds, (opts) =>
                this.allCollections({
                    ids: opts.ids,
                    extend: ['label'],
                }),
            );

            const collectionData = result.data;

            const labelIds = new Set(
                collectionData
                    .reduce<Array<string | undefined>>(
                        (sum, product) => [
                            ...sum,
                            product.extend?.label?.itemLabelId,
                            product.extend?.label?.tileLabelId,
                        ],
                        [],
                    )
                    .filter(is.string),
            );

            const labelsResult = await chunkBulkRequest(
                Array.from(labelIds),
                this.allLabels,
            );

            const labels = labelsResult.data;

            return collectionData.map(
                (collection): SectionCollection => ({
                    ...collection,
                    classification: Classification.Collection,
                    tileLabel: labels.find(
                        (label) =>
                            label.id === collection.extend?.label?.tileLabelId,
                    ),
                    itemLabel: labels.find(
                        (label) =>
                            label.id === collection.extend?.label?.itemLabelId,
                    ),
                }),
            );
        };

        // Async function to fetch all products listed in the children array.
        const fetchProducts = async (
            productsIds: string[],
        ): Promise<SectionProduct[]> => {
            // Page element collection may have no children that are products.
            // If this is the case we want to avoid making a request.
            if (productsIds.length < 1) {
                return [];
            }

            const result = await chunkBulkRequest(productsIds, (opts) =>
                this.allProducts({
                    ids: opts.ids,
                    extend: ['label'],
                }),
            );

            const productData = result.data;

            const labelIds = new Set(
                productData
                    .reduce<Array<string | undefined>>(
                        (sum, product) => [
                            ...sum,
                            product.extend?.label?.itemLabelId,
                            product.extend?.label?.tileLabelId,
                        ],
                        [],
                    )
                    .filter(is.string),
            );

            const allAgeRatingIds = new Set(
                showAgeRatings
                    ? productData
                          .map((product) => product.ageRating)
                          .filter(is.string)
                    : [],
            );

            const [labelsResult, ageRatingsResult] = await Promise.all([
                chunkBulkRequest(Array.from(labelIds), this.allLabels),
                chunkBulkRequest(
                    Array.from(allAgeRatingIds),
                    this.getAllAgeRatings,
                ),
            ]);

            const ageRatings = ageRatingsResult.data;
            const labels = labelsResult.data;

            return productData.map(
                (product): SectionProduct => ({
                    ...product,
                    classification: Classification.Product,
                    rating: ageRatings.find(
                        (rating) => rating.id === product.ageRating,
                    ),
                    tileLabel: labels.find(
                        (label) =>
                            label.id === product.extend?.label?.tileLabelId,
                    ),
                    itemLabel: labels.find(
                        (label) =>
                            label.id === product.extend?.label?.itemLabelId,
                    ),
                }),
            );
        };

        const [productResults, collectionResults] = itemIds.reduce(
            ([pIds, cIds], itemId) => {
                const [type, id] = itemId.split('_');

                if (type === 'product' && id) pIds.add(id);
                if (type === 'collection' && id) cIds.add(id);

                return [pIds, cIds];
            },
            [new Set<string>(), new Set<string>()],
        );

        const [productResponse, collectionResponse] = await Promise.all([
            fetchProducts(Array.from(productResults)),
            fetchCollections(Array.from(collectionResults)),
        ]);

        const bucket = [...collectionResponse, ...productResponse];

        const data: SectionChildren[] = itemIds
            .map((id) => {
                const itemId = id.replace(/[a-z]+_/, '');
                return bucket.find((i) => i.id === itemId);
            })
            .filter(is.notUndefined);

        return ok({
            status: 200,
            body: {
                data,
            },
        });
    };

    /* ------------------------------ Subscription ------------------------------ */

    /**
     * getSubscription
     *
     * Get the Subscription
     *
     * @param { bust } - Override to force the cache not to be used
     * @returns An object containing subscription details
     */
    getSubscription = (
        options?: ExtendRequestOptions<Array<'price-band'>>,
        { bust }: { bust?: boolean } = {},
    ) =>
        this.getFirstCachedResource(
            () =>
                this.get(urls.subscription, {
                    validator: SubscriptionResponse,
                })
                    .withQueries(mapOptionsToStrings(options))
                    .send(),
            {
                cache: {
                    key: 'subscription',
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.menu.expire || DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* -------------------------------- Subscription Purchase -------------------------------- */

    /**
     * createSubscriptionIntent
     *
     * Creates a purchase intent object on the server and returns the id here
     *
     * @param productId - The ID of the product being purchased
     * @returns Subscription intent object
     */
    createSubscriptionIntent = (interval: RenewalPeriod, items: string[]) =>
        this.post(urls.svod.stripe, {
            validator: SubscriptionIntent,
        })
            .withBody({ interval, items })
            .send();

    /**
     * getSubscriptionCard
     *
     * Get the card details for the current user
     *
     * @returns
     */
    getSubscriptionCard = () =>
        this.get(urls.svod.getPaymentMethod, {
            validator: SubscriptionPaymentMethod,
        }).send();

    /**
     * getUpdateCardIntent
     *
     * Get the intent to update the card details for the current user
     *
     * @returns
     */
    getUpdateCardIntent = () =>
        this.post(urls.svod.updatePaymentMethodIntent, {
            validator: UpdateCardIntent,
        }).send();

    /**
     * getRequiresActionIntent
     *
     * Get the intent to resolve required action for the current user
     *
     * @param id - The ID of the item to retrieve
     * @returns
     */
    getRequiresActionIntent = (id: string) =>
        this.post(urls.svod.requiresActionIntent, {
            validator: RequiresActionIntent,
        })
            .withParams({ id })
            .send();

    /**
     * cancelSubscription
     *
     * Cancel the current subscription
     *
     * @param id - The ID of the subscription to cancel
     * @returns
     */
    cancelSubscription = (id: string) =>
        this.post(urls.svod.cancel, {
            validator: NoContentResponse,
        })
            .withParams({ id })
            .send();

    /* ---------------------------------- Theme --------------------------------- */

    /**
     * getTheme
     *
     * Get the current Theme object
     *
     * @param { bust } - Override to force the cache not to be used
     * @returns The Theme object
     */
    getTheme = ({ bust }: { bust?: boolean } = {}): Promise<
        RequestResult<Theme>
    > =>
        this.getFirstCachedResource(
            () => this.get(urls.theme, { validator: Theme }).send(),
            {
                cache: {
                    key: 'theme',
                },
                bust,
                expiry: (_, config) =>
                    dates
                        .calculateDurationFromNow(
                            config?.cache.theme.expire ||
                                DEFAULT_CACHE_DURATION,
                        )
                        .toISOString(),
            },
        );

    /* ---------------------------------- User ---------------------------------- */

    /**
     * getCurrentUser
     *
     * Get the user object
     *
     * @returns The current User
     */
    getCurrentUser = () =>
        this.get(urls.user.get, {
            validator: User,
        })
            .withQueries({
                extend: ['subscription'],
            })
            .send();

    /**
     * updateCurrentUser
     *
     * Updates the current User's details
     *
     * @param user - Updated User fields
     * @returns A User object
     */
    updateCurrentUser = (user: User) =>
        this.put(urls.user.update, {
            validator: User,
        })
            .withBody({
                ...user,
            })
            .send();

    /**
     * getUserPayments
     *
     * Fetches the payments made by the current user
     */
    getUserPayments = () =>
        this.get(urls.user.payments, {
            validator: UserPaymentsResponse,
        }).send();

    /* -------------------------------------------------------------------------- */
    /* --------------------------------- Helpers -------------------------------- */
    /* -------------------------------------------------------------------------- */

    /* ------------------------------- Build Page ------------------------------- */

    /**
     * buildSection
     *
     * Requests and merges all the information needed to display a page element.
     *
     * Fetches the collection data that the page element wants to render as well
     * as any children that the collection contains.
     *
     * @param collectionId - The ID for the collection the page element wants to render
     * @returns A resolved Section object
     */
    buildSection = async (
        collectionId: string,
        showAgeRatings: boolean,
    ): Promise<Result<Section, ResponseSchemaInvalidFail>> => {
        // Try to get the collection data. If there’s a problem, propagate the
        // unsuccessful Result up the call stack.
        const collectionResult = await this.getCollection(collectionId, {
            extend: ['label'],
        });
        if (!collectionResult.ok) return collectionResult;

        // Otherwise, get the actual collection data from the Result.
        const collectionResponse = collectionResult.payload;

        // According to the letter of the API swagger, it is possible for the children
        // property to be undefined. This is a simple fallback to fix that.
        const collectionChildren = collectionResponse.body.children || [];

        // Reduce the children array to get the ids of any products or collections
        // within them that need fetching.
        const { collections, products } = collectionChildren.reduce<{
            collections: string[];
            products: string[];
        }>(
            (res, child) => {
                switch (child.classification) {
                    case 'collection':
                        return {
                            ...res,
                            collections: [...res.collections, child.id],
                        };
                    case 'product':
                        return {
                            ...res,
                            products: [...res.products, child.id],
                        };
                    default:
                        return res;
                }
            },
            {
                collections: [],
                products: [],
            },
        );

        // Async function to fetch all collections listed in the children array.
        const fetchCollections = async (collectionIds: string[]) => {
            // Page element collection may have no children that are collections.
            // If this is the case we want to avoid making a request.
            if (collections.length < 1) {
                return [];
            }

            const result = await chunkBulkRequest(collectionIds, (opts) =>
                this.allCollections({
                    ids: opts.ids,
                    extend: ['label'],
                }),
            );

            const collectionData = result.data;

            const labelIds = new Set(
                collectionData
                    .reduce<Array<string | undefined>>(
                        (sum, product) => [
                            ...sum,
                            product.extend?.label?.itemLabelId,
                            product.extend?.label?.tileLabelId,
                        ],
                        [],
                    )
                    .filter(is.string),
            );

            const labelsResult = await chunkBulkRequest(
                Array.from(labelIds),
                this.allLabels,
            );

            const labels = labelsResult.data;

            return collectionData.map((product) => ({
                ...product,
                tileLabel: labels.find(
                    (label) => label.id === product.extend?.label?.tileLabelId,
                ),
                itemLabel: labels.find(
                    (label) => label.id === product.extend?.label?.itemLabelId,
                ),
            }));
        };

        // Async function to fetch all products listed in the children array.
        const fetchProducts = async (productsIds: string[]) => {
            // Page element collection may have no children that are products.
            // If this is the case we want to avoid making a request.
            if (productsIds.length < 1) {
                return [];
            }

            const result = await chunkBulkRequest(productsIds, (opts) =>
                this.allProducts({
                    ids: opts.ids,
                    extend: ['label'],
                }),
            );

            const productData = result.data;

            const labelIds = new Set(
                productData
                    .reduce<Array<string | undefined>>(
                        (sum, product) => [
                            ...sum,
                            product.extend?.label?.itemLabelId,
                            product.extend?.label?.tileLabelId,
                        ],
                        [],
                    )
                    .filter(is.string),
            );

            const allAgeRatingIds = new Set(
                showAgeRatings
                    ? productData
                          .map((product) => product.ageRating)
                          .filter(is.string)
                    : [],
            );

            const [labelsResult, ageRatingsResult] = await Promise.all([
                chunkBulkRequest(Array.from(labelIds), this.allLabels),
                chunkBulkRequest(
                    Array.from(allAgeRatingIds),
                    this.getAllAgeRatings,
                ),
            ]);

            const ageRatings = ageRatingsResult.data;
            const labels = labelsResult.data;

            const productDataWithAgeRating = productData.map((product) => ({
                ...product,
                rating: ageRatings.find(
                    (rating) => rating.id === product.ageRating,
                ),
                tileLabel: labels.find(
                    (label) => label.id === product.extend?.label?.tileLabelId,
                ),
                itemLabel: labels.find(
                    (label) => label.id === product.extend?.label?.itemLabelId,
                ),
            }));

            return productDataWithAgeRating;
        };

        // Fetch the products and collections simultaneously.
        const [collectionsResponse, productsResponse] = await Promise.all([
            fetchCollections(collections),
            fetchProducts(products),
        ]);

        // We use the bucket as an array to `find()` from when trying to replace
        // product/collection ids with fully resolved products/collections.
        const bucket = [...collectionsResponse, ...productsResponse];

        // Reduce, to produce an array representing the fully resolved children
        // of the page element's collection.
        const children = collectionChildren.reduce<typeof bucket>(
            (res, child) => {
                const item = bucket.find(
                    (bucketItem) => bucketItem.id === child.id,
                );
                // If an item isn't found we remove it from the children array.
                return item
                    ? [
                          ...res,
                          { ...item, classification: child.classification },
                      ]
                    : res;
            },
            [],
        );

        const response = {
            ...collectionResponse.body,
            children,
        };

        // Check that the Section is valid.
        const validatedResponse = Section.safeParse(response);

        // Return a successful `Result<Section, ResponseSchemaInvalidFail>` if
        // the response passed validation.
        if (validatedResponse.success) return ok(validatedResponse.data);

        // Otherwise return an unsuccessful `Result<Section, ResponseSchemaInvalidFail>`,
        // which signifies that the response did not pass validation.
        return err(
            new ResponseSchemaInvalidFail({
                key: 'error.client.schema-mismatch',
                name: 'Client Schema Mismatch',
                description:
                    'Response returned does not match schema after filter',
                data: {
                    response,
                    error: validatedResponse.error,
                },
            }),
        );
    };
}
